import { useEffect, useRef, useState } from 'react';
import { AssetReviewPanel } from './AssetReviewPanel';
import { useCrucibleClient } from '../transport/context.js';
import { Button } from './ui/button';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';

/**
 * Persistent overlay for the #32 BUILD-time, step-by-step asset review.
 *
 * The build kickoff fires `runBuildAssetTask`, whose review-launch wrapper pushes
 * `asset-review:ready`; the engine review server then surfaces each generated
 * asset for approve / redirect / Done. This overlay listens for that event and
 * renders the review panel on TOP of whatever page the user is on while the build
 * runs — so the stage-by-stage review follows the build wherever they are.
 *
 * It must live in the workspace that owns the build. After create the app
 * auto-navigates to that workspace, which is the only route that mounts this
 * workspace-scoped surface. The Dashboard must not subscribe or invoke art IPC.
 *
 * `suppressed` lets a host hide it while that host owns its own review surface
 * (e.g. the CreateGameDialog owns the create-time review while it is open).
 */
export function BuildReviewOverlay({ suppressed = false }: { suppressed?: boolean }) {
  const client = useCrucibleClient();
  const [buildReview, setBuildReview] = useState<{ gameId: string } | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const unsub =
      client.subscribe?.('asset-review:ready', (event) => {
        if (restoreFocusRef.current === null && document.activeElement instanceof HTMLElement) {
          restoreFocusRef.current = document.activeElement;
        }
        setBuildReview({ gameId: event.gameId });
      }) ?? (() => undefined);
    return () => unsub();
  }, [client]);

  if (suppressed || !buildReview) return null;
  return (
    <Dialog open onOpenChange={(open) => !open && setBuildReview(null)}>
      <DialogContent
        aria-modal="true"
        className="w-full max-w-xl p-6"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          restoreFocusRef.current?.focus();
          restoreFocusRef.current = null;
        }}
      >
        <DialogTitle className="sr-only">Review your game art</DialogTitle>
        <DialogDescription className="sr-only">
          Review generated art for this project. Closing this dialog does not stop generation.
        </DialogDescription>
        <AssetReviewPanel gameId={buildReview.gameId} onDone={() => setBuildReview(null)} />
        <div className="flex items-center justify-between gap-4 border-t border-white/10 pt-4">
          <p className="text-xs text-white/50">
            Closing keeps generation running. Open Art to resume reviewing.
          </p>
          <DialogClose asChild>
            <Button variant="secondary">Not now</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
